// src/modules/orion-shell/OrionOrb.tsx
//
// El símbolo de Orion (anillo) «vivo»: alterna entre forma FÍSICA (anillo sólido
// con degradado) y PARTÍCULAS (el mismo anillo disuelto en puntos que respiran).
// Canvas 2D, sin librerías; colores = tokens de marca de la org (--acc1/--acc2).
// prefers-reduced-motion → anillo sólido quieto. Se pausa con la pestaña oculta.
import { useEffect, useRef } from 'react';

interface Props {
  size?: number;
  /** 0 = calmado · 1 = activo (hover / agente pensando): late más y se disuelve más */
  energy?: number;
  className?: string;
}

const N = 72; // partículas: ligero

function cssColor(el: Element, name: string, fallback: string): string {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  if (!v) return fallback;
  // Los tokens pueden venir como «R G B» (para rgb(var(--x)/a)) o como color completo.
  return /^\d+\s+\d+\s+\d+$/.test(v) ? `rgb(${v.replace(/\s+/g, ',')})` : v;
}

export function OrionOrb({ size = 44, energy = 0, className }: Props) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const energyRef = useRef(energy);
  energyRef.current = energy;

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = size * dpr;
    cv.height = size * dpr;
    ctx.scale(dpr, dpr);

    const c1 = cssColor(cv, '--acc1', '#7C3AED');
    const c2 = cssColor(cv, '--acc2', '#4338CA');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const C = size / 2;
    const R = size * 0.3; // radio del anillo
    const W = size * 0.15; // grosor del anillo
    const seeds = Array.from({ length: N }, (_, i) => ({
      a: (i / N) * Math.PI * 2,
      r: (Math.random() - 0.5) * W * 0.9,
      ph: Math.random() * Math.PI * 2,
      sp: 0.6 + Math.random() * 0.8,
    }));

    let raf = 0;
    let t0 = performance.now();
    let mix = 0; // 0 físico · 1 partículas (suavizado)

    const draw = (now: number) => {
      const t = (now - t0) / 1000;
      const e = energyRef.current;
      // Ciclo lento físico ↔ partículas (~9 s); con energía se queda más disuelto.
      const cycle = (Math.sin(t * 0.7) + 1) / 2;
      const target = reduce ? 0 : Math.min(1, cycle * 0.85 + e * 0.6);
      mix += (target - mix) * 0.06;
      const rot = reduce ? 0 : t * (0.25 + e * 0.9);
      const breath = reduce ? 0 : Math.sin(t * 1.6) * 0.04 * (1 + e);

      ctx.clearRect(0, 0, size, size);
      const g = ctx.createLinearGradient(0, 0, size, size);
      g.addColorStop(0, c1);
      g.addColorStop(1, c2);

      // Forma física: anillo con degradado (el símbolo de la marca).
      if (mix < 0.99) {
        ctx.save();
        ctx.globalAlpha = 1 - mix;
        ctx.strokeStyle = g;
        ctx.lineWidth = W;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.arc(C, C, R * (1 + breath), rot, rot + Math.PI * 2);
        ctx.stroke();
        // brillo sutil en el pliegue
        ctx.globalAlpha = (1 - mix) * 0.35;
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(C, C, R * (1 + breath) - W / 2 + 1, rot - 0.9, rot - 0.2);
        ctx.stroke();
        ctx.restore();
      }

      // Partículas: el mismo anillo disuelto, cada punto respira a su ritmo.
      if (mix > 0.01) {
        ctx.save();
        ctx.fillStyle = g;
        for (const s of seeds) {
          const wob = Math.sin(t * s.sp + s.ph);
          const spread = mix * (W * 0.6 + e * W * 0.5);
          const rr = R * (1 + breath) + s.r * mix + wob * spread * 0.5;
          const a = s.a + rot + wob * 0.04 * mix;
          const x = C + Math.cos(a) * rr;
          const y = C + Math.sin(a) * rr;
          ctx.globalAlpha = mix * (0.55 + 0.45 * ((wob + 1) / 2));
          ctx.beginPath();
          ctx.arc(x, y, size * (0.022 + 0.012 * ((wob + 1) / 2)), 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      if (!reduce) raf = requestAnimationFrame(draw);
    };

    const onVis = () => {
      if (document.hidden) cancelAnimationFrame(raf);
      else if (!reduce) { t0 = performance.now() - 1000; raf = requestAnimationFrame(draw); }
    };
    raf = requestAnimationFrame(draw);
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelAnimationFrame(raf); document.removeEventListener('visibilitychange', onVis); };
  }, [size]);

  return <canvas ref={ref} style={{ width: size, height: size }} className={className} aria-hidden />;
}
